/**
 * "Is this ARRAYPATH path curve one this package can place copies ALONG, or
 * only along its plan SHADOW?" — the ONE truth for the frame half of the
 * command's eligibility, beside `./heavyPolylineCurveRefusal.ts`'s subtype half.
 *
 * ## The defect this exists to close
 *
 * @codex PR #785 round 16, P2 ("Preserve Z when sampling tilted array paths").
 * `./pathSampleOcsFrame.ts::liftSampleToWcs` computes the FULL WCS sample and
 * then returns only its `x` / `y`, and `apps/web/.../state/arrayReducers.ts`
 * translates each clone by that remaining pair. For a path whose plane is
 * TILTED out of WCS XY the pair is the curve's plan shadow, and the shadow is
 * not the curve:
 *
 *  - it is not an ISOMETRY of it, so the arc-length walk that decided WHERE to
 *    divide is measured along one curve while the copies are spaced along a
 *    shorter one — every division lands at the wrong parameter; and
 *  - in the limit it is DEGENERATE. Codex's own case: extrusion `(0, 1, 0)`
 *    puts the OCS Y axis along WCS Z, so an OCS path running along it has a
 *    shadow that is a single point and EVERY copy stacks on the source.
 *
 * A confidently wrong array, in other words, rather than a missing one — which
 * is exactly the shape CLAUDE.md ПРИОРИТЕТ #1 forbids and the shape
 * `./heavyPolylineCurveRefusal.ts` already declines a 3D polyline for. This
 * predicate says so for the REST of the path kinds, from the same truth.
 *
 * ## Why a refusal and not the three-axis placement
 *
 * The three-axis placement is the better answer and is what AutoCAD does — its
 * ARRAYPATH reference lists 3D polyline and helix among the valid path curves,
 * so a tilted or climbing path is ordinary there. It is not reachable from
 * here, and the reason is structural rather than a matter of effort: this
 * package's entity transformer is 2D END TO END. `./geometryTransforms.ts`
 * declares `TransformFn = (p: Point2D) => Point2D`, `transformEntityCoords`
 * hands every coordinate field through it, and its `transformPoint3D` helper
 * exists precisely to CARRY a group-30 elevation past a map that cannot touch
 * it. Nothing in MOVE / COPY / ROTATE / MIRROR / SCALE moves an entity in Z.
 * Giving ARRAYPATH a Z delta therefore means widening that one shared
 * transformer and every one of its entity branches — the single 3D-placement
 * slice the whole editor is missing, not an ARRAYPATH detail, and far past what
 * one command's fix may quietly decide on its own.
 *
 * So this is CLAUDE.md ПРИОРИТЕТ #1's one allowed exception — a KNOWN
 * INCOMPLETE SUBSYSTEM, declined VISIBLY and documented with its completion
 * plan — and deliberately NOT a silent narrowing. COMPLETING IT: widen
 * `TransformFn` to a 3D point map (or add a 3D sibling) so
 * `transformEntityCoords` can move an entity in Z; give `PathSample.point` a Z
 * channel and `liftSampleToWcs` its already-computed `point.z`; measure
 * `pathTotalLength` over 3D chords for the kinds whose samples are genuinely
 * 3D; replace `arrayReducers.ts`'s two-axis delta with three; and decide the
 * ALIGNMENT question this file's `tilted-plane` arm also covers — a tangent
 * standing perpendicular to WCS XY has no plan direction at all, so
 * `alignItems` would need a full 3D frame (AutoCAD's own "Align items" +
 * "Z direction" pair) rather than the single `rotateEntityAngles` angle the
 * reducer applies today. Then this arm is deleted and `./heavyPolylineCurve
 * Refusal.ts`'s `"polyline-3d"` ARRAYPATH arm with it: they are the same gap
 * seen from two sides.
 *
 * ## Scope: the FRAME, never the subtype
 *
 * `./heavyPolylineCurveRefusal.ts` answers "which heavy POLYLINE subtype is a
 * curve ARRAYPATH may read at all" and this file answers "which PLANE the
 * sampler's answer is faithful in". Keeping them apart is what lets each stay
 * exhaustive over its own question: a mesh has no traversal whatever its
 * normal, and a tilted CIRCLE is refused for a reason that has nothing to do
 * with heavy polylines. `arrayReducers.ts` asks both, in that order.
 */

import type { OcsAxes } from "@dxf-ai-editor/drawing-model";

import { pathOcsPlane } from "./pathSampleOcsFrame";
import type { PathEntity } from "./pathSampleTypes";

/**
 * Why `ARRAYPATH` declines this path curve's FRAME. `null` means the curve is
 * confined to a plane parallel to WCS XY, where the sampler's two-axis answer
 * is the curve itself rather than a projection of it.
 *
 * Two members because the two kinds of path state their frame in genuinely
 * different fields, and a user looking at one is not looking at the other:
 *
 *  - `"tilted-plane"` — a CIRCLE / ARC / LWPOLYLINE / 2D heavy POLYLINE whose
 *    group-210 extrusion is not parallel to WCS Z, so its stored OCS
 *    coordinates lift OUT of the XY plane.
 *  - `"sloped-line"` — a LINE whose group-30 start and end elevations differ.
 *    A LINE carries no OCS lift at all ({@link pathOcsPlane} returns `null` for
 *    it, because its group 210 does not move its WCS endpoints), yet it is the
 *    one path kind that can be non-planar on its own: `pathTotalLength` measures
 *    `Math.hypot(dx, dy)` for it, which is SHORT by the climb, and a vertical
 *    LINE measures zero — the same total collapse the tilted plane produces.
 */
export type ArrayPathPlaneRefusal = "tilted-plane" | "sloped-line";

/**
 * `null` when `ARRAYPATH` can place copies along `entity` itself, otherwise the
 * reason it can only place them along the curve's plan shadow. See the module
 * header for the completion plan behind each reason.
 *
 * Total and pure, and the SINGLE predicate the document reducer's guard reads —
 * the two-site shape `./tableScaleRefusal.ts` established and
 * `./heavyPolylineCurveRefusal.ts` follows, so an eligibility test and the
 * reason reported for it can never drift apart.
 */
export function arrayPathPlaneRefusal(
  entity: PathEntity,
): ArrayPathPlaneRefusal | null {
  // FUNCTION-LOCAL, not a module constant, for the reason
  // `./pathSampleOcsFrame.ts::liftSampleToWcs` records for its own two
  // factors: a module-level `const` is evaluated once per worker at IMPORT
  // time, so a mutation of it is a STATIC mutant the Stryker vitest runner
  // cannot activate, and the gate reports a false "Survived" for a threshold
  // this file's own suites do pin. Evaluated per call it is a dynamic, and
  // therefore killable, mutant.
  //
  // The value is `drawing-model`'s own `isDefaultExtrusion` epsilon: both this
  // test and that one ask whether a UNIT vector's component is zero, so the
  // same 1e-9 answers both, and a normal this package calls tilted can never
  // be one `computeOcsAxes` already short-circuited as the WCS default.
  const UNIT_COMPONENT_EPS = 1e-9;

  if (entity.type === "Line") {
    const dx = entity.end.x - entity.start.x;
    const dy = entity.end.y - entity.start.y;
    const dz = (entity.end.z ?? 0) - (entity.start.z ?? 0);
    const length = Math.hypot(dx, dy, dz);
    // A degenerate (zero-length) LINE is NOT a sloped one: it has no direction
    // to be sloped in, and the sampler already answers for it with its own
    // start point and a zero tangent — the pre-existing degenerate answer this
    // guard must not convert into a refusal.
    if (length <= UNIT_COMPONENT_EPS) return null;
    // RELATIVE, not an absolute coordinate test: the quantity that decides
    // whether the plan shadow is the curve is the DIRECTION's z component, and
    // a direction is scale-free. An absolute `|dz| < eps` would call a
    // millimetre-long line with a micron of climb planar and a kilometre-long
    // one with the same micron sloped, which is backwards.
    return Math.abs(dz) / length > UNIT_COMPONENT_EPS ? "sloped-line" : null;
  }

  const plane = pathOcsPlane(entity);
  // No plane to lift through means the stored coordinates are already WCS and
  // already planar in XY — the overwhelmingly common drawing, which keeps its
  // existing answer untouched.
  if (plane === null) return null;
  return isXyParallelPlane(plane.axes, UNIT_COMPONENT_EPS)
    ? null
    : "tilted-plane";
}

/**
 * Whether lifting an in-plane `(x, y)` through `axes` leaves WCS Z alone.
 *
 * `ocsToWcs` gives the lifted point's `z` as `x·ax.z + y·ay.z + elevation·az.z`,
 * so the lift is a plane-to-plane map into a CONSTANT WCS elevation exactly
 * when `ax.z` and `ay.z` both vanish — and then, the basis being orthonormal,
 * its restriction to XY is an isometry and the sampler's two-axis answer IS the
 * curve. `ax` and `ay` are unit vectors, so comparing a component of them to
 * `eps` is the same scale-free test the LINE arm applies to its direction.
 *
 * The question is asked of the PLANE, not of the particular curve drawn in it,
 * and that is a deliberate, named CONSERVATIVE margin rather than an oversight.
 * A tilted plane meets WCS XY in a line, and a curve that happens to run ONLY
 * along that line — a straight polyline, nothing else, since a bulge or a
 * CIRCLE / ARC projects to an ellipse from any tilt — does have an isometric
 * shadow and would array correctly today. Admitting it would mean asking every
 * segment direction of every chain instead of asking the frame once: a
 * per-curve predicate that must be kept in step with the sampler's own
 * traversal (bulges, closure, the heavy family's record runs), to license a
 * case that is a measure-zero coincidence in real drawings. Refusing the
 * tilted plane whole is the answer a user can predict from what they can see —
 * "this path is not flat in the current plan" — and it is the one that cannot
 * become wrong if the sampler learns a new segment kind.
 *
 * Tested on the AXES rather than on the extrusion because the axes are the map
 * that actually runs: `computeOcsAxes` normalises, picks a seed axis either
 * side of the Arbitrary Axis Algorithm's `1/64` threshold and crosses twice,
 * and this asks the question of the RESULT of all that rather than of an input
 * the question would have to be re-derived from. It admits `(0, 0, -1)` — the
 * mirrored normal is a genuinely different frame, but a frame whose `ax` /
 * `ay` still lie in XY, and one @codex PR #785 round 15 already made the
 * sampler place correctly.
 */
function isXyParallelPlane(axes: OcsAxes, eps: number): boolean {
  return Math.abs(axes.ax.z) <= eps && Math.abs(axes.ay.z) <= eps;
}
